import React, { useEffect, useState } from 'react';
import { Card, CardContent, FormControl, InputLabel, makeStyles, MenuItem, Select, LinearProgress } from '@material-ui/core';
import Fetcher, { URLBuild } from '../../services/fetcher';
import settings from '../../settings';
import { ResponsiveContainer, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { ogoneStatYears, ogoneStatDefaultYear } from './consts'


/// Filters header

const useFilterStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'center',
    boxShadow: 'none'
  },
  filters: {
    display: 'flex',
	  justifyContent: 'space-evenly',
	  alignItems: 'center'
  },
  dropDown: {
    justifyContent: 'flex-start',
    minWidth: "20vh",
  },
  title: {
    fontSize: "1rem"
  },
}));

export interface FiltersInterface {
  year: number
}

interface Props {
  filters: FiltersInterface,
  setFilters: React.Dispatch<React.SetStateAction<FiltersInterface>>
};

const FiltersHeader = ({filters, setFilters}: Props) => {
  const classes = useFilterStyles();

  const handleActiveChange = (event: React.ChangeEvent<{ value: unknown }>, key: 'year') => {
    setFilters((oldFilters) => {
      const newFilters = {...oldFilters};
      newFilters[key] = parseInt(String(event.target.value), 10);
      return newFilters;
    });
  };

  return (
    <Card className={classes.container}>
      <CardContent>
        <div className={classes.filters}>
          <FormControl variant="outlined" className={classes.dropDown}>
            <InputLabel id="activeLabel">Année</InputLabel>
            <Select
              labelId="activeLabel"
              id="activeSelect"
              value={filters.year}
              onChange={(event) => handleActiveChange(event, 'year')}
              label="Année"
            >
              {ogoneStatYears.map((year, i) =>
                <MenuItem key={i} value={year.value}>{year.displayValue}</MenuItem>
              )}
            </Select>
          </FormControl>
          <div className={classes.title}>Clients Ogone actifs pour { filters.year }</div>
        </div>
      </CardContent>
    </Card>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(2),
    paddingTop: '10px',
    textAlign: "center"
  }
}));

interface ogoneStats {
  [year: string]: {
    [month: string]: {
      [date: string]: {
        isoDate: string
        tt: number
        [subType: string]: number | any
      }
    }
  }
}

const lines = [
  { dataKey: "ogone_card", name: "clients carte bancaire", color: "#82ca9d", fill: 'url(#colorPv)' },
  { dataKey: "ogone_card_forecast", name: "clients carte bancaire prévisionnel", color: "#8884d8", fill: 'url(#colorUv)' },
];

const fakeData = (new Array(12)).fill(1).map((_, index) => {
  const fakeLine = {
    name: String(index + 1),
    nameXAxis: String(index + 1),
    ogone_card: 0 as number | undefined,
    ogone_card_forecast: 0 as number | undefined
  };
  return fakeLine;
});

const OgoneAllChart = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(0);
  const [filters, setFilters] = useState<FiltersInterface>({year: ogoneStatDefaultYear as number});
  const [apiData, setApiData] = useState<{card: ogoneStats | null}>({ card: null });
  const [data, setData] = useState(fakeData);

  useEffect(() => {
    if (!apiData.card) {
      setData(fakeData)
      return
    }
    const monthData = []
    const cardYear = apiData.card[filters.year] || {}
    for (const month of Object.keys(cardYear).sort()) {
      const cardMonth = cardYear[month]
      const lastDay = Object.keys(cardMonth).sort().pop() || ''

      const isPast = new Date(cardMonth[lastDay].isoDate).getTime() > Date.now()
      const lastMonth = new Date().setMonth(new Date().getMonth() - 1)
      const isForecast = new Date(cardMonth[lastDay].isoDate).getTime() >= lastMonth
      
      monthData.push({
        name: month,
        nameXAxis: month,
        ogone_card: isPast ? undefined : cardMonth[lastDay].tt || 0,
        ogone_card_forecast: isForecast ? cardMonth[lastDay].tt || 0 : undefined
      })
    }
    
    setData(monthData)
  }, [apiData, filters])

  useEffect(() => {
    const asyncFunc = async () => {
      setLoading((prev) => prev + 1);

      const cardRes = await Fetcher.get(URLBuild(settings.PaymentProvider.paymentApi + '/stat-ogone/card', {})) as ogoneStats

      setApiData({card: cardRes});
      setLoading((prev) => prev - 1);
    }
    asyncFunc()
  }, [])

  return (
    <Card className={classes.container}>
      <FiltersHeader filters={filters} setFilters={setFilters}></FiltersHeader>
      <div style={{height: '60vh'}}>
        <ResponsiveContainer>
          <ComposedChart
            width={500}
            height={400}
            data={data}
            margin={{
              top: 10,
              right: 20,
              bottom: 16,
              left: 0
            }}
          >
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="nameXAxis" />
            <YAxis type="number" domain={[0, 6000]} />
            <CartesianGrid stroke="#d5d5d5" strokeDasharray="3 3" />
            <Tooltip labelFormatter={l => `${l}/${filters.year}`} />
            <Legend wrapperStyle={{ bottom: '6px' }} />
            {
              lines.map((line) => {
                return <Area type="monotone" key={line.dataKey} dataKey={line.dataKey} name={line.name} stroke={line.color} fillOpacity={1} fill={line.fill} />
              })
            }
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {!!loading && <LinearProgress color="secondary" />}
    </Card>
  );
}

export default OgoneAllChart;